import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// One-time nuke of stale service-worker + caches for users who have an
// installed PWA carrying an old index.html with a strict CSP (the CSP was
// blocking YouTube's iframe_api.js from doing `new Function()`). Bumping
// APP_RESET_VERSION forces every client to fully reset once.
const APP_RESET_VERSION = "6";
(async () => {
  try {
    if (localStorage.getItem("app_reset_v") === APP_RESET_VERSION) return;
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
    localStorage.setItem("app_reset_v", APP_RESET_VERSION);
    // Fresh load with no SW / cache
    window.location.reload();
  } catch (_) {
    // If anything fails, just record the version so we don't loop
    try { localStorage.setItem("app_reset_v", APP_RESET_VERSION); } catch (__) {}
  }
})();

// Register Service Worker (PWA) — enables install + background audio persistence
if ("serviceWorker" in navigator && window.location.protocol === "https:") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // If a new worker is waiting, activate it immediately so the fresh
        // index.html (and its updated CSP / iframe layout) takes effect on
        // the very next navigation instead of after two full reloads.
        if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              nw.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch(() => {});

    // When the active worker changes (SKIP_WAITING kicked in), reload once
    // so the freshly-cached shell is what's rendering.
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}
